import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent } from '@fuse/components/alert';
import { TranslocoModule } from '@ngneat/transloco';
import { map } from 'rxjs';

import { AuthService } from 'app/core/auth/auth.service';

import {
  MfaEnrollStepComponent,
  MfaQrSizeService,
} from './mfa-enroll-step.component';
import { MfaRecoveryCodesComponent } from './mfa-recovery-codes.component';

export interface MfaSetupDialogResult {
  enrolled: boolean;
}

/**
 * Configuración de MFA/TOTP como modal (follow-up LION-631). Porta la lógica de
 * enrolamiento de `auth-mfa-setup` (enrollMfaStart → QR + secreto, primer código
 * TOTP → enrollMfaConfirm → recovery codes) a un MatDialog standalone con OnPush
 * y signals, sin tocar la ruta `/mfa-setup` existente.
 *
 * Vive en `core/auth/` (junto al step-up) porque es TRANSVERSAL: lo consumen
 * tanto el profile como MfaEnrollmentService, que lo abre desde el interceptor
 * ante un 403 MFA_ENROLLMENT_REQUIRED. Un import de `core/` hacia `modules/`
 * sería una violación de capas.
 *
 * Contrato (lo consumen el profile y MfaEnrollmentService):
 *   - selector: 'auth-mfa-setup-dialog'
 *   - sin MAT_DIALOG_DATA
 *   - close({ enrolled: true }) cuando se completan los recovery codes
 *   - close() si el usuario cierra antes de terminar
 *
 * El rediseño EXPONE los tres estados que ya existían (indicador de pasos,
 * skeletons en vez de spinner, error de arranque con reintento) y convierte el
 * paso 2 en una tarea con salida explícita; no agrega lógica de flujo.
 */
@Component({
  selector: 'auth-mfa-setup-dialog',
  templateUrl: './mfa-setup-dialog.component.html',
  /*
   * El host cierra la cadena de altura. Entre `.mat-mdc-dialog-surface` (que en
   * teléfono mide 100dvh por el `panelClass`) y el wrapper del template hay un
   * elemento `<auth-mfa-setup-dialog>` que, sin esto, no tiene altura propia:
   * el `h-full` del wrapper resuelve contra `auto`, el cuerpo `flex-1 min-h-0`
   * no tiene contra qué recortarse y el scroll se escapa al contenedor. Es el
   * bug original reintroducido un nivel más arriba, y ningún `min-h-0` lo tapa.
   */
  styles: [
    ':host { display: flex; flex-direction: column; min-height: 0; height: 100%; }',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: fuseAnimations,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    FuseAlertComponent,
    TranslocoModule,
    MfaEnrollStepComponent,
    MfaRecoveryCodesComponent,
  ],
})
export class MfaSetupDialogComponent implements OnInit {
  private readonly _dialogRef = inject(
    MatDialogRef<MfaSetupDialogComponent, MfaSetupDialogResult>
  );
  private readonly _authService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly code = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(/^[0-9]{6}$/)],
  });

  // Paso 0: pidiendo el secreto/QR. Paso 1: QR + secreto + código.
  // Paso 2: ya confirmado → recovery codes.
  readonly loadingStart = signal(true);
  readonly otpauthUri = signal<string | null>(null);
  readonly secret = signal<string | null>(null);
  readonly recoveryCodes = signal<string[] | null>(null);
  readonly submitting = signal(false);

  /** El arranque falló: el modal sólo ofrece cerrar o reintentar. */
  readonly startError = signal(false);
  /** Clave i18n FIJA del error del código; nunca el body del backend. */
  readonly codeError = signal<string | null>(null);
  /** El usuario confirmó que guardó los recovery codes. */
  readonly acknowledged = signal(false);

  /**
   * Validez del código como signal. El template es OnPush: leer `code.invalid`
   * pelado no vuelve a renderizar el botón cuando el valor cambia sin un evento
   * de DOM (por ejemplo al pegar los 6 dígitos o desde un spec).
   */
  readonly codeValid = toSignal(
    this.code.statusChanges.pipe(map(() => this.code.valid)),
    { initialValue: false }
  );

  /**
   * Lado del cuadrado del skeleton del QR. Sale de la MISMA fuente que el ancho
   * del `<qrcode>`, así que no puede volver a desalinearse del tamaño real de
   * la caja cuando el ancho cambia entre teléfono y escritorio.
   */
  readonly qrBoxSize = inject(MfaQrSizeService).boxSize;

  readonly currentStep = computed(() => (this.recoveryCodes() ? 2 : 1));

  readonly headerTitle = computed(() =>
    this.recoveryCodes() ? 'mfa_setup_recovery_title' : 'mfa_setup_title'
  );

  /**
   * El subtítulo acompaña al estado. En el error de arranque va en `null`: el
   * mensaje ya lo muestra el `fuse-alert` del cuerpo y repetirlo sería decir lo
   * mismo dos veces en el mismo modal.
   */
  readonly headerSubtitle = computed(() => {
    if (this.startError()) {
      return null;
    }

    if (this.recoveryCodes()) {
      return 'mfa_setup_recovery_hint';
    }

    return this.loadingStart() ? 'mfa_setup_generating' : 'mfa_setup_scan_hint';
  });

  readonly headerIcon = computed(() => {
    if (this.startError()) {
      return 'heroicons_outline:exclamation-triangle';
    }

    return this.recoveryCodes()
      ? 'heroicons_outline:key'
      : 'heroicons_outline:device-phone-mobile';
  });

  readonly headerIconClass = computed(() => {
    if (this.startError()) {
      return 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900 dark:text-red-200';
    }

    if (this.recoveryCodes()) {
      return 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900 dark:text-green-200';
    }

    return 'text-primary border-gray-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-800';
  });

  ngOnInit(): void {
    // Diferimos el arranque un macrotask: la request HTTP activa el FuseLoadingBar
    // global, y dispararla durante el primer CD del dialog provoca NG0100
    // (ExpressionChangedAfterItHasBeenChecked) en el loading bar.
    setTimeout(() => this._startEnrollment());
  }

  /**
   * Paso 0 → 1: pide al backend el secreto + otpauth URI para mostrar el QR.
   */
  private _startEnrollment(): void {
    this.loadingStart.set(true);
    this.startError.set(false);
    this._authService
      .enrollMfaStart()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          this.otpauthUri.set(res.otpauthUri);
          this.secret.set(res.secret);
          this.loadingStart.set(false);
        },
        error: () => {
          this.loadingStart.set(false);
          this.startError.set(true);
        },
      });
  }

  /**
   * Error de arranque: reintenta SIN cerrar el modal (antes había que cerrarlo
   * y reabrirlo desde el perfil).
   */
  retry(): void {
    this._startEnrollment();
  }

  /**
   * Paso 1 → 2: confirma el enrolamiento con el primer código TOTP.
   */
  confirm(): void {
    if (this.code.invalid || this.submitting()) {
      this.code.markAsTouched();
      return;
    }

    this.submitting.set(true);
    this.code.disable();
    this.codeError.set(null);

    this._authService
      .enrollMfaConfirm(this.code.value)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          // Paso 2: mostrar los recovery codes (única vez que se ven). Cerrar
          // acá por backdrop/ESC los descartaría en silencio, así que el modal
          // pasa a salir SÓLO por el botón.
          this.recoveryCodes.set(res.recoveryCodes);
          this._dialogRef.disableClose = true;
          this.submitting.set(false);
        },
        error: () => {
          this.submitting.set(false);
          this.code.enable();
          this.codeError.set('mfa_setup_code_invalid');
        },
      });
  }

  /**
   * Paso 2: el usuario guardó sus recovery codes → MFA quedó activado.
   */
  done(): void {
    this._dialogRef.close({ enrolled: true });
  }

  cancel(): void {
    this._dialogRef.close();
  }
}
